import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, inject } from '@angular/core';
import { OrderManagerService } from '../../../lib/services/order-manager/order-manager.service';
import { takeUntil } from 'rxjs';
import { Subject } from 'rxjs';
import { Order } from '../../interfaces/orders/order';
import { eOrderType } from '../../enums';
import { MatDialog } from '@angular/material/dialog';
import { OrderHistoryComponent } from '../order-history/order-history.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'lib-lien-lifetime',
    templateUrl: './lien-lifetime.component.html',
    styleUrls: ['./lien-lifetime.component.css'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LienLifetimeComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  registrationDate: Date | null | undefined = null;
  expiryDate: Date | null | undefined = null;
  progress = 0;
  private unsubscribe$ = new Subject<void>();
  timeRemaining = '';
  isInfiniteTerm = false;
  isDischarged = false;
  isUnknown = false;
  isLastOrder: boolean | null | undefined = true;
  order: Order | null | undefined = null;

  constructor(protected orderManager: OrderManagerService,
    protected dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.orderManager.currentOrder
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(order => {
        this.order = order;
        const resultIndex = this.getResultIndex(order);
        
        if(order.orderTypeID === eOrderType.reregistration) {
          this.registrationDate = new Date(order.lien?.registrationResults?.at(resultIndex)?.registrationDate ?? order.orderStatusUpdatedDate!);
        } else {
          this.registrationDate = new Date(order.referenceRegistrationInformation?.originalRegistrationDate ?? 
            order.lien!.registrationResults?.[resultIndex]?.registrationDate ?? 
            order.orderStatusUpdatedDate!);
        }

        this.isInfiniteTerm = order.lien?.isInfiniteTerm ?? false;
        this.isDischarged = order.orderGroup?.lastOrderType === eOrderType.discharge;
        this.isLastOrder = order.isLastOrder;
        if (!this.isDischarged) {
          if (!this.isInfiniteTerm) {
            this.expiryDate = this.getExpiryDate(order);
          }

          if (this.expiryDate) {
            this.calculateProgress();
            this.calculateTimeRemaining();
          }
        }
    });
  }

  calculateProgress(): void {
    if (this.isInfiniteTerm) {
      this.progress = 100;
    } else {
      const now = new Date();
      
      const endOfExpiryDate = new Date(this.expiryDate!);
      endOfExpiryDate.setHours(23, 59, 59, 999);
      
      const totalDuration = endOfExpiryDate.getTime() - this.registrationDate!.getTime();
      const elapsed = now.getTime() - this.registrationDate!.getTime();
      const rawProgress = (elapsed / totalDuration) * 100;
      
      this.progress = Math.min(rawProgress, 100);
    }
  }

  calculateTimeRemaining(): void {
    if (this.isInfiniteTerm) {
      this.timeRemaining = this.transloco.translate('lienLifetime.permanent');
    } else if (this.expiryDate) {
      const now = new Date();

      const endOfExpiryDate = new Date(this.expiryDate);
      endOfExpiryDate.setHours(23, 59, 59, 999);

      const remainingTime = endOfExpiryDate.getTime() - now.getTime();
      if (remainingTime > 0) {
        const daysRemaining = Math.ceil(remainingTime / (1000 * 60 * 60 * 24));
        this.timeRemaining = daysRemaining > 1
          ? this.transloco.translate('lienLifetime.daysRemaining', { count: daysRemaining })
          : this.transloco.translate('lienLifetime.dayRemaining', { count: daysRemaining });
      } else {
        this.timeRemaining = this.transloco.translate('lienLifetime.expired');
      }
    }
  }

  ngOnDestroy(): void {
      // Signal all subscriptions to complete
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
  }

  calculateExpiryDateFromTerm(registrationDate: Date, term: number): Date {
    const expiryDate = new Date(registrationDate); // Clone the original date
    expiryDate.setFullYear(expiryDate.getFullYear() + term);
    return expiryDate;
  }

  getExpiryDate(order: Order) {
    this.isUnknown = false;
    const resultIndex = this.getResultIndex(order);
    // If we have a requested expiry date use that
    if (order.lien?.expiryDate) {
      return new Date(order.lien.expiryDate);
    } else if (order.lien?.registrationResults?.at(resultIndex)?.expiryDate) {
      return new Date(order.lien.registrationResults.at(resultIndex)!.expiryDate!);
    } else if (order.referenceRegistrationInformation?.expiryDate) {
      if (order.lien?.term) {
        return this.calculateExpiryDateFromTerm(order.referenceRegistrationInformation.expiryDate, order.lien.term)
      } else {
        return order.referenceRegistrationInformation?.expiryDate;
      }
    } else if (order.lien?.term) {
      // otherwise, calculate expiry date based on term
      return this.calculateExpiryDateFromTerm(this.registrationDate!, order.lien!.term!);
    }
    else {
      this.isUnknown = true;
      return null;
    }
  }

  openOrderHistory() {
    this.dialog.open(OrderHistoryComponent, {
      data: this.order,
      minWidth: '800px'
    });
  }

  private getResultIndex(order: Order): number {
    let resultIndex = (order.lien?.registrationResults?.length ?? 0) - 1;

    if (resultIndex < 0) {
      resultIndex = 0;
    }

    return resultIndex;
  }
}
